import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Mic, Sparkles, CheckCircle2, AlertCircle, Upload as UploadIcon, Plus, X, ListChecks, GitBranch, Layers, Square, Building2, Package, AudioLines, Users2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { STAGES, STAGE_LABEL } from "@/lib/format";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import { LEAD_SOURCES } from "@/lib/leadOptions";

// 36.docx: single source of truth — same 21 sources as Leads page / backend.
const LEAD_SOURCES_FOR_DEFAULT = LEAD_SOURCES.map((s) => [s.key, s.label]);

function VisitingCardFlow() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Lead-mode extras (default — visiting cards become marketing leads)
  const [saveMode, setSaveMode] = useState("marketing"); // marketing | lead | client
  const [leadSource, setLeadSource] = useState("exhibition_conference");
  const [leadStatus, setLeadStatus] = useState("new");
  const [leadEvent, setLeadEvent] = useState("");
  const [leadNotes, setLeadNotes] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setExtracted(null);
    setDuplicate(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const extract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/visiting-card", fd);
      setExtracted(data.extracted || {});
      setDuplicate(data.duplicate);
      setLeadEvent("");
      setLeadNotes("");
      toast.success("Card extracted — review and confirm");
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setExtracted(null); setDuplicate(null);
    setLeadEvent(""); setLeadNotes("");
  };

  const save = async () => {
    if (!extracted?.name && !extracted?.phone && !extracted?.email) { toast.error("Need at least name/phone/email"); return; }
    setSaving(true);
    try {
      if (saveMode === "marketing") {
        await api.post("/marketing-contacts", {
          name: extracted.name || "Unknown",
          designation: extracted.designation || null,
          company_name: extracted.company || null,
          email: extracted.email || null,
          phone: extracted.phone || null,
          industry: null,
          location: extracted.address || null,
          source: "visiting_card",
          status: "new",
          notes: [
            leadNotes,
            extracted.address ? `Address: ${extracted.address}` : null,
            extracted.website ? `Website: ${extracted.website}` : null,
          ].filter(Boolean).join("\n") || null,
        });
        toast.success("Marketing contact saved · top of funnel");
      } else if (saveMode === "lead") {
        await api.post("/leads", {
          name: extracted.name || "Unknown",
          designation: extracted.designation || null,
          company_name: extracted.company || null,
          email: extracted.email || null,
          phone: extracted.phone || null,
          source: leadSource,
          status: leadStatus,
          event_name: leadEvent || null,
          sync_contact: false,
          notes: [
            leadNotes,
            extracted.address ? `Address: ${extracted.address}` : null,
            extracted.website ? `Website: ${extracted.website}` : null,
          ].filter(Boolean).join("\n") || null,
        });
        toast.success("Lead saved · ready for outreach");
      } else {
        await api.post("/clients", {
          company_name: extracted.company || extracted.name || "Unknown",
          contact_person: extracted.name || null,
          email: extracted.email || null,
          phone: extracted.phone || null,
          website: extracted.website || null,
          notes: [
            leadNotes,
            extracted.address ? `Address: ${extracted.address}` : null,
          ].filter(Boolean).join("\n") || null,
        });
        toast.success("Client created");
      }
      reset();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Upload card</p>
          <label className="flex flex-col items-center justify-center gap-3 p-10 rounded-3xl border-2 border-dashed border-border/80 bg-muted/30 cursor-pointer hover:bg-muted/50 transition tap" data-testid="vcard-dropzone">
            {preview ? (
              <img src={preview} alt="preview" className="max-h-56 rounded-xl object-contain" />
            ) : (
              <>
                <Camera className="h-8 w-8 text-primary" />
                <span className="font-semibold">Tap to upload visiting card</span>
                <span className="text-xs text-muted-foreground">JPG, PNG or PDF</span>
              </>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onFile} data-testid="vcard-file-input" />
          </label>
          {file && (
            <Button onClick={extract} disabled={loading} className="w-full mt-4 h-11 rounded-2xl tap" data-testid="vcard-extract-button">
              {loading ? "Extracting…" : <><Sparkles className="h-4 w-4 mr-1" />Extract with AI</>}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Step 2 — Review & confirm</p>
            {extracted && (
              <div className="flex items-center gap-1 rounded-2xl border border-border/60 bg-muted/30 p-0.5" role="tablist">
                <button
                  type="button"
                  onClick={() => setSaveMode("marketing")}
                  className={`tap rounded-xl px-3 py-1 text-xs font-bold transition ${
                    saveMode === "marketing" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="vcard-mode-marketing"
                >
                  Marketing
                </button>
                <button
                  type="button"
                  onClick={() => setSaveMode("lead")}
                  className={`tap rounded-xl px-3 py-1 text-xs font-bold transition ${
                    saveMode === "lead" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="vcard-mode-lead"
                >
                  Lead
                </button>
                <button
                  type="button"
                  onClick={() => setSaveMode("client")}
                  className={`tap rounded-xl px-3 py-1 text-xs font-bold transition ${
                    saveMode === "client" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="vcard-mode-client"
                >
                  Client
                </button>
              </div>
            )}
          </div>
          {!extracted ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Upload a card to see AI suggestions here. Nothing is saved without your confirmation.</p>
          ) : (
            <div className="space-y-3">
              {duplicate && (
                <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 flex gap-2 items-start" data-testid="vcard-duplicate-warning">
                  <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    Possible duplicate: <strong>{duplicate.name}</strong> ({duplicate.email || duplicate.phone})
                  </div>
                </div>
              )}
              {["name", "designation", "company", "email", "phone", "website", "address"].map((k) => (
                <div key={k}>
                  <Label className="text-xs font-semibold capitalize">{k}</Label>
                  <Input
                    className="rounded-2xl h-11 mt-1.5"
                    value={extracted[k] || ""}
                    onChange={(e) => setExtracted({ ...extracted, [k]: e.target.value })}
                    data-testid={`vcard-${k}-input`}
                  />
                </div>
              ))}

              {saveMode === "marketing" ? (
                <div className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700">Marketing contact details</p>
                  <p className="text-[11px] text-muted-foreground -mt-2">Top-of-funnel · use for outreach campaigns. Convert to Lead when interest is shown.</p>
                  <div>
                    <Label className="text-xs font-semibold">Notes / context</Label>
                    <Textarea
                      className="rounded-2xl mt-1.5 bg-white"
                      rows={2}
                      value={leadNotes}
                      onChange={(e) => setLeadNotes(e.target.value)}
                      placeholder="Where you got the card, anything relevant for outreach"
                      data-testid="vcard-marketing-notes"
                    />
                  </div>
                </div>
              ) : saveMode === "lead" ? (
                <div className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700">Lead details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Source</Label>
                      <Select value={leadSource} onValueChange={setLeadSource}>
                        <SelectTrigger className="rounded-2xl h-11 mt-1.5 bg-white" data-testid="vcard-lead-source"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {LEAD_SOURCES_FOR_DEFAULT.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Status</Label>
                      <Select value={leadStatus} onValueChange={setLeadStatus}>
                        <SelectTrigger className="rounded-2xl h-11 mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {["new", "contacted", "qualified", "dropped"].map((k) => <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Event / campaign name</Label>
                    <Input
                      className="rounded-2xl h-11 mt-1.5 bg-white"
                      value={leadEvent}
                      onChange={(e) => setLeadEvent(e.target.value)}
                      placeholder="e.g. ACMA Reliability Conclave 2026"
                      data-testid="vcard-lead-event"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Notes / context</Label>
                    <Textarea
                      className="rounded-2xl mt-1.5 bg-white"
                      rows={2}
                      value={leadNotes}
                      onChange={(e) => setLeadNotes(e.target.value)}
                      placeholder="What did you discuss / interest area / where you met"
                      data-testid="vcard-lead-notes"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700">Client details</p>
                  <p className="text-[11px] text-muted-foreground -mt-2">Creates a new Client record owned by you — the company on the card becomes the client name.</p>
                  <div>
                    <Label className="text-xs font-semibold">Notes / context</Label>
                    <Textarea
                      className="rounded-2xl mt-1.5 bg-white"
                      rows={2}
                      value={leadNotes}
                      onChange={(e) => setLeadNotes(e.target.value)}
                      placeholder="Anything relevant about this client"
                      data-testid="vcard-client-notes"
                    />
                  </div>
                </div>
              )}

              <Button onClick={save} disabled={saving} className="w-full h-11 rounded-2xl tap mt-2" data-testid="vcard-save-button">
                <CheckCircle2 className="h-4 w-4 mr-1" /> {saving ? "Saving…" : `Confirm & save ${saveMode === "lead" ? "lead" : saveMode === "marketing" ? "marketing contact" : "client"}`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MeetingFlow() {
  const [text, setText] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealId, setDealId] = useState("");
  const [clientId, setClientId] = useState("");
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const extract = async () => {
    if (text.trim().length < 10) { toast.error("Paste a meeting note first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/meeting-summary", { raw_text: text, deal_id: dealId || null, client_id: clientId || null });
      const e = data.extracted || {};
      // Ensure follow_ups is always an editable array
      if (!Array.isArray(e.follow_ups) || e.follow_ups.length === 0) {
        e.follow_ups = e.next_action
          ? [{ title: e.next_action, with_whom: "", due_date: e.follow_up_date || "", channel: "call" }]
          : [];
      } else {
        e.follow_ups = e.follow_ups.map((f) => ({
          title: f.title || "",
          with_whom: f.with_whom || "",
          due_date: f.due_date || "",
          channel: f.channel || "call",
        }));
      }
      setExtracted(e);
      toast.success("Extracted — review and confirm");
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  };

  const updateFu = (idx, patch) => {
    setExtracted((prev) => {
      const list = [...(prev.follow_ups || [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, follow_ups: list };
    });
  };

  const removeFu = (idx) => {
    setExtracted((prev) => {
      const list = [...(prev.follow_ups || [])];
      list.splice(idx, 1);
      return { ...prev, follow_ups: list };
    });
  };

  const addFu = () => {
    setExtracted((prev) => ({
      ...prev,
      follow_ups: [...(prev.follow_ups || []), { title: "", with_whom: "", due_date: "", channel: "call" }],
    }));
  };

  const save = async () => {
    if (!extracted) return;
    setSaving(true);
    try {
      // 1) Save activity (the meeting log)
      const summary = extracted.discussion || "Meeting note";
      const details = [
        extracted.pain_points?.length ? `Pain points:\n- ${extracted.pain_points.join("\n- ")}` : null,
        extracted.requirements?.length ? `Requirements:\n- ${extracted.requirements.join("\n- ")}` : null,
        extracted.objections?.length ? `Objections:\n- ${extracted.objections.join("\n- ")}` : null,
      ].filter(Boolean).join("\n\n");
      await api.post("/activities", {
        type: "meeting",
        deal_id: dealId || null,
        client_id: clientId || null,
        summary,
        details,
      });

      // 2) Save EVERY confirmed follow-up
      const validFus = (extracted.follow_ups || []).filter((f) => f.title && f.title.trim());
      let createdFu = 0;
      for (const f of validFus) {
        const due = f.due_date
          ? new Date(f.due_date + "T12:00:00").toISOString()
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
        await api.post("/followups", {
          title: f.with_whom ? `${f.title} — with ${f.with_whom}` : f.title,
          due_date: due,
          deal_id: dealId || null,
          client_id: clientId || null,
        });
        createdFu += 1;
      }

      // 3) Update pipeline stage on the deal if the user accepted a suggestion
      if (dealId && extracted.pipeline_stage_suggestion) {
        try {
          await api.post(`/deals/${dealId}/stage`, { stage: extracted.pipeline_stage_suggestion });
        } catch {
          toast.error("Stage move failed (you can update manually).");
        }
      }

      const parts = ["Memory captured ✓"];
      if (createdFu) parts.push(`${createdFu} follow-up${createdFu > 1 ? "s" : ""} scheduled`);
      if (dealId && extracted.pipeline_stage_suggestion) parts.push(`pipeline → ${STAGE_LABEL[extracted.pipeline_stage_suggestion] || extracted.pipeline_stage_suggestion}`);
      toast.success(parts.join(" · "));
      setText(""); setExtracted(null);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Paste meeting note</p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder="Paste your NeoSapien (or any) meeting summary here…"
            className="rounded-2xl"
            data-testid="meeting-text-input"
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Select value={clientId || "__none"} onValueChange={(v) => setClientId(v === "__none" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-11"><SelectValue placeholder="Client (optional)" /></SelectTrigger>
              <SelectContent className="rounded-2xl"><SelectItem value="__none">No client</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={dealId || "__none"} onValueChange={(v) => setDealId(v === "__none" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-11"><SelectValue placeholder="Deal (optional)" /></SelectTrigger>
              <SelectContent className="rounded-2xl"><SelectItem value="__none">No deal</SelectItem>{deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={extract} disabled={loading} className="w-full mt-4 h-11 rounded-2xl tap" data-testid="meeting-extract-button">
            {loading ? "Extracting…" : <><Sparkles className="h-4 w-4 mr-1" />Extract with AI</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 2 — Review & confirm</p>
          {!extracted ? (
            <p className="py-12 text-center text-sm text-muted-foreground">AI suggestions appear here. You confirm before anything is saved.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Discussion</Label>
                <Textarea className="rounded-2xl mt-1.5" rows={3} value={extracted.discussion || ""} onChange={(e) => setExtracted({ ...extracted, discussion: e.target.value })} data-testid="meeting-discussion" />
              </div>
              {[
                ["pain_points", "Pain points"],
                ["requirements", "Requirements"],
                ["objections", "Objections"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs font-semibold">{l}</Label>
                  <Textarea
                    className="rounded-2xl mt-1.5"
                    rows={2}
                    value={(extracted[k] || []).join("\n")}
                    onChange={(e) => setExtracted({ ...extracted, [k]: e.target.value.split("\n").filter(Boolean) })}
                    placeholder="One per line"
                  />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Next action (legacy)</Label>
                  <Input className="rounded-2xl h-11 mt-1.5" value={extracted.next_action || ""} onChange={(e) => setExtracted({ ...extracted, next_action: e.target.value })} data-testid="meeting-next-action" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Sentiment</Label>
                  <Select value={extracted.sentiment || "neutral"} onValueChange={(v) => setExtracted({ ...extracted, sentiment: v })}>
                    <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Follow-ups list — editable */}
              <div className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-primary" />
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-700">Follow-ups extracted</p>
                    <Badge variant="outline" className="bg-white text-primary-700 border-primary-200 text-[10px]">
                      {(extracted.follow_ups || []).length}
                    </Badge>
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={addFu} className="h-7 rounded-xl text-xs" data-testid="meeting-add-followup">
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {(extracted.follow_ups || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3 text-center">
                    No follow-ups detected. Add one if needed before saving.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(extracted.follow_ups || []).map((f, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl bg-white border border-border/60 p-3 space-y-2"
                        data-testid={`meeting-fu-row-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div className="sm:col-span-3">
                              <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Action</Label>
                              <Input
                                className="rounded-xl h-10 mt-1 text-sm"
                                value={f.title}
                                placeholder="e.g. Send revised quotation"
                                onChange={(ev) => updateFu(idx, { title: ev.target.value })}
                                data-testid={`meeting-fu-title-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">With whom</Label>
                              <Input
                                className="rounded-xl h-10 mt-1 text-sm"
                                value={f.with_whom || ""}
                                placeholder="Mr. Sharma"
                                onChange={(ev) => updateFu(idx, { with_whom: ev.target.value })}
                                data-testid={`meeting-fu-who-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Due</Label>
                              <Input
                                type="date"
                                className="rounded-xl h-10 mt-1 text-sm"
                                value={f.due_date || ""}
                                onChange={(ev) => updateFu(idx, { due_date: ev.target.value })}
                                data-testid={`meeting-fu-due-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Channel</Label>
                              <Select value={f.channel || "call"} onValueChange={(v) => updateFu(idx, { channel: v })}>
                                <SelectTrigger className="rounded-xl h-10 mt-1 text-sm" data-testid={`meeting-fu-channel-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                  <SelectItem value="call">Call</SelectItem>
                                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="meeting">Meeting</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFu(idx)}
                            aria-label="Remove follow-up"
                            className="h-8 w-8 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 tap shrink-0"
                            data-testid={`meeting-fu-remove-${idx}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pipeline stage suggestion */}
              <div className="rounded-2xl border border-amber-200/60 bg-amber-50/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <GitBranch className="h-4 w-4 text-amber-700" />
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800">Pipeline update</p>
                  {extracted.pipeline_stage_suggestion && (
                    <Badge variant="outline" className="bg-white text-amber-800 border-amber-200 text-[10px] uppercase">AI suggested</Badge>
                  )}
                </div>
                <Select
                  value={extracted.pipeline_stage_suggestion || "__none"}
                  onValueChange={(v) => setExtracted({ ...extracted, pipeline_stage_suggestion: v === "__none" ? null : v })}
                >
                  <SelectTrigger className="rounded-2xl h-11 bg-white" data-testid="meeting-pipeline-stage">
                    <SelectValue placeholder="Don't change pipeline stage" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <SelectItem value="__none">Don't change pipeline stage</SelectItem>
                    {STAGES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {extracted.stage_reason && extracted.pipeline_stage_suggestion && (
                  <p className="text-xs text-amber-900/80 italic mt-2">"{extracted.stage_reason}"</p>
                )}
                {!dealId && extracted.pipeline_stage_suggestion && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Link a deal above to apply this stage change automatically.
                  </p>
                )}
              </div>

              <Button onClick={save} disabled={saving} className="w-full h-11 rounded-2xl tap" data-testid="meeting-save-button">
                <CheckCircle2 className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Confirm & update memory"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BulkVisitingCardFlow() {
  /**
   * Drop multiple visiting card photos → AI extracts each in parallel →
   * unified review table with editable rows → bulk save as Leads.
   *
   * Each row has its own status: pending | extracting | ready | failed | saving | saved | save_failed
   */
  const [rows, setRows] = useState([]); // { id, file, preview, status, error, fields, source, status_lead }
  const [extracting, setExtracting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  // Common defaults applied when a row's per-row override is empty
  const [commonSource, setCommonSource] = useState("exhibition_conference");
  const [commonStatus, setCommonStatus] = useState("new");
  const [commonEvent, setCommonEvent] = useState("");
  const [commonNotes, setCommonNotes] = useState("");

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newRows = files.map((f) => ({
      id: `${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      preview: URL.createObjectURL(f),
      status: "pending",
      error: null,
      fields: { name: "", designation: "", company: "", email: "", phone: "", website: "", address: "" },
      source: "", // empty → fall back to commonSource
    }));
    setRows((prev) => [...prev, ...newRows]);
    // Reset input so the same file can be re-added if needed
    e.target.value = "";
  };

  const extractAll = async () => {
    const pending = rows.filter((r) => r.status === "pending" || r.status === "failed");
    if (!pending.length) { toast.message("Nothing to extract"); return; }
    setExtracting(true);

    // Mark all as extracting in one update
    setRows((prev) => prev.map((r) => (r.status === "pending" || r.status === "failed") ? { ...r, status: "extracting", error: null } : r));

    // Parallel extraction (Gemini handles concurrency on the backend)
    await Promise.all(pending.map(async (row) => {
      try {
        const fd = new FormData();
        fd.append("file", row.file);
        const { data } = await api.post("/ai/visiting-card", fd);
        const e = data.extracted || {};
        setRows((prev) => prev.map((r) => r.id === row.id
          ? { ...r, status: "ready", fields: {
              name: e.name || "",
              designation: e.designation || "",
              company: e.company || "",
              email: e.email || "",
              phone: e.phone || "",
              website: e.website || "",
              address: e.address || "",
            }}
          : r));
      } catch (err) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "failed", error: fmtError(err) } : r));
      }
    }));

    setExtracting(false);
    toast.success(`Extracted ${pending.length} card${pending.length > 1 ? "s" : ""} — review and save`);
  };

  const updateField = (rowId, field, value) => {
    setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, fields: { ...r.fields, [field]: value } } : r));
  };

  const setRowSource = (rowId, src) => {
    setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, source: src } : r));
  };

  const removeRow = (rowId) => {
    setRows((prev) => {
      const r = prev.find((x) => x.id === rowId);
      if (r?.preview) URL.revokeObjectURL(r.preview);
      return prev.filter((x) => x.id !== rowId);
    });
  };

  const saveAll = async () => {
    const saveable = rows.filter((r) => r.status === "ready" || r.status === "save_failed");
    if (!saveable.length) { toast.error("Nothing ready to save. Extract first."); return; }
    setSavingAll(true);

    let saved = 0;
    let failed = 0;
    for (const row of saveable) {
      // Need at least one of name/email/phone to save
      const f = row.fields;
      if (!f.name && !f.email && !f.phone) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "save_failed", error: "Need name / email / phone" } : r));
        failed += 1;
        continue;
      }
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "saving", error: null } : r));
      try {
        const noteParts = [
          commonNotes,
          f.address ? `Address: ${f.address}` : null,
          f.website ? `Website: ${f.website}` : null,
        ].filter(Boolean);
        await api.post("/leads", {
          name: f.name || "Unknown",
          designation: f.designation || null,
          company_name: f.company || null,
          email: f.email || null,
          phone: f.phone || null,
          source: row.source || commonSource,
          status: commonStatus,
          event_name: commonEvent || null,
          sync_contact: false,
          notes: noteParts.join("\n") || null,
        });
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "saved", error: null } : r));
        saved += 1;
      } catch (err) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "save_failed", error: fmtError(err) } : r));
        failed += 1;
      }
    }
    setSavingAll(false);
    if (saved && !failed) toast.success(`${saved} lead${saved > 1 ? "s" : ""} saved · ready for outreach`);
    else if (saved && failed) toast.message(`${saved} saved · ${failed} failed (review red rows)`);
    else toast.error(`${failed} failed — see error chips below each row`);
  };

  const removeAllSaved = () => {
    setRows((prev) => {
      prev.filter((r) => r.status === "saved").forEach((r) => r.preview && URL.revokeObjectURL(r.preview));
      return prev.filter((r) => r.status !== "saved");
    });
  };

  const stats = {
    total: rows.length,
    pending: rows.filter((r) => r.status === "pending").length,
    ready: rows.filter((r) => r.status === "ready").length,
    saved: rows.filter((r) => r.status === "saved").length,
    failed: rows.filter((r) => r.status === "failed" || r.status === "save_failed").length,
  };

  const STATUS_BADGE = {
    pending: { label: "Queued", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
    extracting: { label: "Extracting…", cls: "bg-violet-50 text-violet-700 border-violet-200 animate-pulse" },
    ready: { label: "Ready", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    failed: { label: "Extract failed", cls: "bg-red-50 text-red-700 border-red-200" },
    saving: { label: "Saving…", cls: "bg-violet-50 text-violet-700 border-violet-200 animate-pulse" },
    saved: { label: "Saved", cls: "bg-primary-50 text-primary-700 border-primary-200" },
    save_failed: { label: "Save failed", cls: "bg-red-50 text-red-700 border-red-200" },
  };

  return (
    <div className="space-y-5">
      {/* Step 1 — multi-file picker */}
      <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Drop multiple cards</p>
          <label className="flex flex-col items-center justify-center gap-3 p-8 sm:p-10 rounded-3xl border-2 border-dashed border-border/80 bg-muted/30 cursor-pointer hover:bg-muted/50 transition tap" data-testid="vcard-bulk-dropzone">
            <Camera className="h-8 w-8 text-primary" />
            <span className="font-semibold text-center">Tap to select cards (multiple)</span>
            <span className="text-xs text-muted-foreground">JPG / PNG / PDF · {rows.length} added</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              className="hidden"
              onChange={onFiles}
              data-testid="vcard-bulk-file-input"
            />
          </label>

          {rows.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={extractAll}
                disabled={extracting || stats.pending + stats.failed === 0}
                className="rounded-2xl tap h-10"
                data-testid="vcard-bulk-extract-button"
              >
                {extracting ? "Extracting…" : <><Sparkles className="h-4 w-4 mr-1" />Extract {stats.pending + stats.failed} card{(stats.pending + stats.failed) === 1 ? "" : "s"}</>}
              </Button>
              <span className="text-xs text-muted-foreground ml-auto">
                {stats.total} added · {stats.ready} ready · {stats.saved} saved · {stats.failed > 0 ? <span className="text-red-700 font-bold">{stats.failed} failed</span> : "0 failed"}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — common defaults */}
      {rows.length > 0 && (
        <Card className="rounded-3xl border-primary-200/60 shadow-soft bg-primary-50/30 overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary-700 mb-3">Step 2 — Common context for all cards</p>
            <p className="text-xs text-muted-foreground mb-4">Applied to every row when you save (per-row source can override).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Default source</Label>
                <Select value={commonSource} onValueChange={setCommonSource}>
                  <SelectTrigger className="rounded-2xl h-11 mt-1.5 bg-white" data-testid="vcard-bulk-common-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {LEAD_SOURCES_FOR_DEFAULT.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Default status</Label>
                <Select value={commonStatus} onValueChange={setCommonStatus}>
                  <SelectTrigger className="rounded-2xl h-11 mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {["new", "contacted", "qualified", "dropped"].map((k) => <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Event / campaign name</Label>
                <Input
                  className="rounded-2xl h-11 mt-1.5 bg-white"
                  value={commonEvent}
                  onChange={(e) => setCommonEvent(e.target.value)}
                  placeholder="e.g. ACMA Reliability Conclave 2026"
                  data-testid="vcard-bulk-common-event"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Notes / context (optional)</Label>
                <Textarea
                  className="rounded-2xl mt-1.5 bg-white"
                  rows={2}
                  value={commonNotes}
                  onChange={(e) => setCommonNotes(e.target.value)}
                  placeholder="What context unifies these cards (booth conversation theme, training topic, etc.)"
                  data-testid="vcard-bulk-common-notes"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — review rows */}
      {rows.length > 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Step 3 — Review & edit each card</p>
              {stats.saved > 0 && (
                <Button variant="ghost" size="sm" onClick={removeAllSaved} className="h-8 rounded-xl text-xs" data-testid="vcard-bulk-clear-saved">
                  Clear {stats.saved} saved
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {rows.map((row, idx) => {
                const sb = STATUS_BADGE[row.status] || STATUS_BADGE.pending;
                return (
                  <div
                    key={row.id}
                    className={`rounded-2xl border bg-card overflow-hidden ${row.status === "saved" ? "border-primary-200/60 bg-primary-50/20" : row.status.includes("failed") ? "border-red-200/60 bg-red-50/30" : "border-border/60"}`}
                    data-testid={`vcard-bulk-row-${idx}`}
                  >
                    <div className="flex items-start gap-3 p-3">
                      <img
                        src={row.preview}
                        alt={`card ${idx + 1}`}
                        className="h-16 w-24 sm:h-20 sm:w-32 rounded-xl object-cover bg-muted shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-bold text-muted-foreground">Card #{idx + 1}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${sb.cls}`}>
                              {sb.label}
                            </Badge>
                            <button
                              type="button"
                              onClick={() => removeRow(row.id)}
                              aria-label="Remove card"
                              className="h-7 w-7 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 tap"
                              data-testid={`vcard-bulk-remove-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {row.error && (
                          <p className="text-[11px] text-red-700 italic mb-1.5" data-testid={`vcard-bulk-error-${idx}`}>{row.error}</p>
                        )}
                        {(row.status === "ready" || row.status === "saving" || row.status === "save_failed") && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                            {[
                              ["name", "Name *"],
                              ["designation", "Designation"],
                              ["company", "Company"],
                              ["phone", "Phone"],
                              ["email", "Email"],
                            ].map(([k, l]) => (
                              <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
                                <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{l}</Label>
                                <Input
                                  className="rounded-xl h-9 mt-0.5 text-sm"
                                  value={row.fields[k] || ""}
                                  onChange={(e) => updateField(row.id, k, e.target.value)}
                                  data-testid={`vcard-bulk-${k}-${idx}`}
                                  disabled={row.status === "saving"}
                                />
                              </div>
                            ))}
                            <div className="sm:col-span-2">
                              <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Source override (optional)</Label>
                              <Select value={row.source || "__inherit"} onValueChange={(v) => setRowSource(row.id, v === "__inherit" ? "" : v)} disabled={row.status === "saving"}>
                                <SelectTrigger className="rounded-xl h-9 mt-0.5 text-sm" data-testid={`vcard-bulk-source-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                  <SelectItem value="__inherit">Use common source ({LEAD_SOURCES_FOR_DEFAULT.find(([k]) => k === commonSource)?.[1] || commonSource})</SelectItem>
                                  {LEAD_SOURCES_FOR_DEFAULT.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {stats.ready > 0 && (
              <Button
                onClick={saveAll}
                disabled={savingAll}
                className="w-full mt-5 h-12 rounded-2xl tap"
                data-testid="vcard-bulk-save-button"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                {savingAll ? "Saving…" : `Save ${stats.ready} lead${stats.ready === 1 ? "" : "s"}`}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BulkMeetingFlow() {
  /**
   * Drop multiple meeting notes (one per textarea, or paste several blocks
   * separated by `---`). Parallel Gemini extraction → unified review of every
   * meeting's discussion + follow-ups + suggested stage moves. Bulk save:
   * 1× /api/activities + N× /api/followups per meeting + optional stage move.
   */
  const [bulkText, setBulkText] = useState("");
  const [rows, setRows] = useState([]); // each row = one meeting
  const [extracting, setExtracting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  // Common context — useful when all the meetings happened on one site visit
  const [commonClientId, setCommonClientId] = useState("");
  const [commonDealId, setCommonDealId] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || []));
    api.get("/deals").then((r) => setDeals(r.data || []));
  }, []);

  const splitIntoNotes = (text) => {
    return text.split(/\n\s*-{3,}\s*\n/).map((s) => s.trim()).filter((s) => s.length >= 10);
  };

  const queueFromText = () => {
    const notes = splitIntoNotes(bulkText);
    if (!notes.length) { toast.error("Paste at least one note (separate multiple notes with --- on its own line)"); return; }
    const newRows = notes.map((text) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text,
      status: "pending", // pending | extracting | ready | failed | saving | saved | save_failed
      error: null,
      extracted: null,
      clientId: commonClientId,
      dealId: commonDealId,
    }));
    setRows((prev) => [...prev, ...newRows]);
    setBulkText("");
    toast.success(`Queued ${newRows.length} meeting note${newRows.length > 1 ? "s" : ""}`);
  };

  const extractAll = async () => {
    const pending = rows.filter((r) => r.status === "pending" || r.status === "failed");
    if (!pending.length) { toast.message("Nothing to extract"); return; }
    setExtracting(true);
    setRows((prev) => prev.map((r) => (r.status === "pending" || r.status === "failed") ? { ...r, status: "extracting", error: null } : r));

    await Promise.all(pending.map(async (row) => {
      try {
        const { data } = await api.post("/ai/meeting-summary", {
          raw_text: row.text,
          deal_id: row.dealId || null,
          client_id: row.clientId || null,
        });
        const e = data.extracted || {};
        if (!Array.isArray(e.follow_ups) || e.follow_ups.length === 0) {
          e.follow_ups = e.next_action ? [{ title: e.next_action, with_whom: "", due_date: e.follow_up_date || "", channel: "call" }] : [];
        } else {
          e.follow_ups = e.follow_ups.map((f) => ({
            title: f.title || "",
            with_whom: f.with_whom || "",
            due_date: f.due_date || "",
            channel: f.channel || "call",
          }));
        }
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "ready", extracted: e } : r));
      } catch (err) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "failed", error: fmtError(err) } : r));
      }
    }));
    setExtracting(false);
    toast.success(`Extracted ${pending.length} meeting note${pending.length > 1 ? "s" : ""} — review and save`);
  };

  const updateRowExtracted = (rowId, patch) => {
    setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, extracted: { ...r.extracted, ...patch } } : r));
  };

  const updateRowFu = (rowId, fuIdx, patch) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const list = [...(r.extracted.follow_ups || [])];
      list[fuIdx] = { ...list[fuIdx], ...patch };
      return { ...r, extracted: { ...r.extracted, follow_ups: list } };
    }));
  };

  const removeRowFu = (rowId, fuIdx) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const list = [...(r.extracted.follow_ups || [])];
      list.splice(fuIdx, 1);
      return { ...r, extracted: { ...r.extracted, follow_ups: list } };
    }));
  };

  const removeRow = (rowId) => setRows((prev) => prev.filter((r) => r.id !== rowId));

  const saveAll = async () => {
    const saveable = rows.filter((r) => r.status === "ready" || r.status === "save_failed");
    if (!saveable.length) { toast.error("Nothing ready to save. Extract first."); return; }
    setSavingAll(true);

    let saved = 0; let failed = 0; let createdFu = 0; let stageMoves = 0;
    for (const row of saveable) {
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "saving", error: null } : r));
      try {
        const e = row.extracted;
        // 1) Save activity (the meeting log)
        const summary = e.discussion || "Meeting note";
        const details = [
          e.pain_points?.length ? `Pain points:\n- ${e.pain_points.join("\n- ")}` : null,
          e.requirements?.length ? `Requirements:\n- ${e.requirements.join("\n- ")}` : null,
          e.objections?.length ? `Objections:\n- ${e.objections.join("\n- ")}` : null,
        ].filter(Boolean).join("\n\n");
        await api.post("/activities", {
          type: "meeting",
          deal_id: row.dealId || null,
          client_id: row.clientId || null,
          summary,
          details,
        });
        // 2) Save every follow-up
        const validFus = (e.follow_ups || []).filter((f) => f.title && f.title.trim());
        for (const f of validFus) {
          const due = f.due_date ? new Date(f.due_date + "T12:00:00").toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
          await api.post("/followups", {
            title: f.with_whom ? `${f.title} — with ${f.with_whom}` : f.title,
            due_date: due,
            deal_id: row.dealId || null,
            client_id: row.clientId || null,
          });
          createdFu += 1;
        }
        // 3) Stage move if both deal linked AND a stage was suggested/picked
        if (row.dealId && e.pipeline_stage_suggestion) {
          try {
            await api.post(`/deals/${row.dealId}/stage`, { stage: e.pipeline_stage_suggestion });
            stageMoves += 1;
          } catch { /* non-fatal */ }
        }
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "saved", error: null } : r));
        saved += 1;
      } catch (err) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: "save_failed", error: fmtError(err) } : r));
        failed += 1;
      }
    }
    setSavingAll(false);
    if (saved && !failed) toast.success(`${saved} meeting${saved > 1 ? "s" : ""} captured · ${createdFu} follow-up${createdFu === 1 ? "" : "s"}${stageMoves ? ` · ${stageMoves} stage move${stageMoves > 1 ? "s" : ""}` : ""}`);
    else toast.message(`${saved} saved · ${failed} failed (review red rows)`);
  };

  const stats = {
    total: rows.length,
    pending: rows.filter((r) => r.status === "pending").length,
    ready: rows.filter((r) => r.status === "ready").length,
    saved: rows.filter((r) => r.status === "saved").length,
    failed: rows.filter((r) => r.status === "failed" || r.status === "save_failed").length,
  };

  const STATUS_BADGE = {
    pending: { label: "Queued", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
    extracting: { label: "Extracting…", cls: "bg-violet-50 text-violet-700 border-violet-200 animate-pulse" },
    ready: { label: "Ready", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    failed: { label: "Extract failed", cls: "bg-red-50 text-red-700 border-red-200" },
    saving: { label: "Saving…", cls: "bg-violet-50 text-violet-700 border-violet-200 animate-pulse" },
    saved: { label: "Saved", cls: "bg-primary-50 text-primary-700 border-primary-200" },
    save_failed: { label: "Save failed", cls: "bg-red-50 text-red-700 border-red-200" },
  };

  return (
    <div className="space-y-5">
      <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Paste multiple meeting notes (separate with <code className="bg-muted px-1.5 py-0.5 rounded">---</code> on its own line)</p>
          <Textarea
            className="rounded-2xl font-mono text-xs"
            rows={8}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"Met Mr. Sharma at Acme. They want a vibration audit by Friday.\n---\nFollow up call with Ms. Patel from BlueOak — schedule demo next Tuesday.\n---\nQuotation discussion with Sterling Cement — they need revised pricing by month-end."}
            data-testid="meeting-bulk-textarea"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs font-semibold">Common client (applied to all)</Label>
              <Select value={commonClientId || "__none"} onValueChange={(v) => setCommonClientId(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="No common client" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Common deal (applied to all)</Label>
              <Select value={commonDealId || "__none"} onValueChange={(v) => setCommonDealId(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="No common deal" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {deals.filter((d) => !commonClientId || d.client_id === commonClientId).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={queueFromText} disabled={!bulkText.trim()} className="rounded-2xl tap" data-testid="meeting-bulk-queue-button">
              <Plus className="h-4 w-4 mr-1" /> Queue notes
            </Button>
            {rows.length > 0 && (
              <Button onClick={extractAll} disabled={extracting || stats.pending + stats.failed === 0} variant="outline" className="rounded-2xl tap" data-testid="meeting-bulk-extract-button">
                {extracting ? "Extracting…" : <><Sparkles className="h-4 w-4 mr-1" />Extract {stats.pending + stats.failed} note{(stats.pending + stats.failed) === 1 ? "" : "s"}</>}
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              {stats.total} queued · {stats.ready} ready · {stats.saved} saved · {stats.failed > 0 && <span className="text-red-700 font-bold">{stats.failed} failed</span>}
            </span>
          </div>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((row, idx) => {
            const sb = STATUS_BADGE[row.status] || STATUS_BADGE.pending;
            const e = row.extracted;
            return (
              <Card key={row.id} className={`rounded-2xl border overflow-hidden ${row.status === "saved" ? "border-primary-200/60 bg-primary-50/20" : row.status.includes("failed") ? "border-red-200/60 bg-red-50/30" : "border-border/60"}`} data-testid={`meeting-bulk-row-${idx}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-bold text-muted-foreground">Note #{idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${sb.cls}`}>{sb.label}</Badge>
                      <button type="button" onClick={() => removeRow(row.id)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 tap" data-testid={`meeting-bulk-remove-${idx}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {row.error && <p className="text-[11px] text-red-700 italic mb-2">{row.error}</p>}
                  <p className="text-xs text-muted-foreground italic line-clamp-2 bg-muted/30 rounded-xl p-2 mb-3">"{row.text.slice(0, 220)}{row.text.length > 220 ? "…" : ""}"</p>

                  {(row.status === "ready" || row.status === "saving" || row.status === "save_failed") && e && (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Discussion</Label>
                        <Textarea className="rounded-xl text-sm mt-0.5" rows={2} value={e.discussion || ""} onChange={(ev) => updateRowExtracted(row.id, { discussion: ev.target.value })} data-testid={`meeting-bulk-discussion-${idx}`} disabled={row.status === "saving"} />
                      </div>
                      <div className="rounded-xl border border-primary-200/60 bg-primary-50/20 p-2.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[10px] uppercase tracking-wider font-bold text-primary-700">Follow-ups ({(e.follow_ups || []).length})</p>
                        </div>
                        <div className="space-y-1.5">
                          {(e.follow_ups || []).map((f, fi) => (
                            <div key={fi} className="flex items-center gap-1.5 bg-white rounded-lg p-1.5">
                              <Input className="rounded-md h-8 text-xs flex-1" value={f.title} onChange={(ev) => updateRowFu(row.id, fi, { title: ev.target.value })} placeholder="Action" data-testid={`meeting-bulk-${idx}-fu-${fi}-title`} disabled={row.status === "saving"} />
                              <Input className="rounded-md h-8 text-xs w-28" value={f.with_whom || ""} onChange={(ev) => updateRowFu(row.id, fi, { with_whom: ev.target.value })} placeholder="Who" disabled={row.status === "saving"} />
                              <Input type="date" className="rounded-md h-8 text-xs w-32" value={f.due_date || ""} onChange={(ev) => updateRowFu(row.id, fi, { due_date: ev.target.value })} disabled={row.status === "saving"} />
                              <button type="button" onClick={() => removeRowFu(row.id, fi)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground" disabled={row.status === "saving"}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      {e.pipeline_stage_suggestion && (
                        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2">
                          <strong>Suggested stage move:</strong> {STAGE_LABEL[e.pipeline_stage_suggestion] || e.pipeline_stage_suggestion}
                          {e.stage_reason && <span className="italic"> — "{e.stage_reason}"</span>}
                          {!row.dealId && <span className="block mt-0.5 text-muted-foreground">(Set a common deal above to apply)</span>}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {stats.ready > 0 && (
            <Button onClick={saveAll} disabled={savingAll} className="w-full h-12 rounded-2xl tap" data-testid="meeting-bulk-save-button">
              <CheckCircle2 className="h-4 w-4 mr-1" /> {savingAll ? "Saving…" : `Save ${stats.ready} meeting${stats.ready === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function VoiceJournalFlow() {
  /**
   * Record audio in-app via MediaRecorder → upload → Whisper transcribe →
   * Gemini extract → review screen with editable client/product/follow-up
   * matching → bulk save (1× journal activity + N× follow-ups + optional stage moves).
   */
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [data, setData] = useState(null); // { transcript, extracted, available_clients, available_products }
  const [saving, setSaving] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MAX_SECONDS = 15 * 60; // 15-minute hard cap (matches Whisper 25MB ceiling at 24kbps Opus → ~2.7MB)

  const start = async () => {
    setPermissionError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      // Force a low Opus bitrate so a full 15-min day-summary stays well under the 25MB Whisper cap.
      // 24 kbps Opus is still highly intelligible for spoken voice.
      const recOpts = { audioBitsPerSecond: 24000 };
      if (mime) recOpts.mimeType = mime;
      const rec = new MediaRecorder(stream, recOpts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start(1000); // emit chunks every 1s
      mediaRecorderRef.current = rec;
      setRecording(true);
      setPaused(false);
      setSeconds(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setData(null);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          // Auto-stop at the 15-minute cap so reps never lose audio they thought was still recording.
          if (next >= MAX_SECONDS) {
            try { rec.stop(); } catch { /* ignore */ }
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
            setRecording(false);
            setPaused(false);
            toast.message("Reached 15-minute cap — stopped automatically.");
            return MAX_SECONDS;
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setPermissionError(err?.message || "Microphone access denied");
      toast.error("Microphone access denied. Allow it in your browser settings and try again.");
    }
  };

  const togglePause = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      setPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
    } else if (rec.state === "paused") {
      rec.resume();
      setPaused(false);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
  };

  const stop = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    setPaused(false);
  };

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
    setData(null);
  };

  const upload = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const ext = (audioBlob.type.includes("mp4") ? "mp4" : audioBlob.type.includes("ogg") ? "ogg" : "webm");
      const fd = new FormData();
      fd.append("file", audioBlob, `voice-journal.${ext}`);
      // Step 1 — transcribe (Whisper). Kept as its own request to stay under ingress 60s timeout.
      const { data: t } = await api.post("/ai/voice-journal/transcribe", fd, { timeout: 120000 });
      // Step 2 — extract structured journal (Gemini) + CRM fuzzy match.
      const { data: resp } = await api.post("/ai/voice-journal/extract", { transcript: t.transcript }, { timeout: 120000 });
      setData(resp);
      toast.success(`Transcribed ${(resp.transcript || "").split(/\s+/).filter(Boolean).length} words — review and save`);
    } catch (err) { toast.error(fmtError(err)); } finally { setUploading(false); }
  };

  const updateExtracted = (patch) => setData((d) => d && { ...d, extracted: { ...d.extracted, ...patch } });

  // Engagement-level helpers
  const updateEngagement = (engIdx, patch) => setData((d) => {
    if (!d) return d;
    const list = [...(d.extracted.engagements || [])];
    list[engIdx] = { ...list[engIdx], ...patch };
    return { ...d, extracted: { ...d.extracted, engagements: list } };
  });
  const removeEngagement = (engIdx) => setData((d) => {
    if (!d) return d;
    const list = [...(d.extracted.engagements || [])];
    list.splice(engIdx, 1);
    return { ...d, extracted: { ...d.extracted, engagements: list } };
  });
  const updateEngagementListItem = (engIdx, key, idx, patch) => setData((d) => {
    if (!d) return d;
    const list = [...(d.extracted.engagements || [])];
    const eng = { ...list[engIdx] };
    const sub = [...(eng[key] || [])];
    sub[idx] = { ...sub[idx], ...patch };
    eng[key] = sub;
    list[engIdx] = eng;
    return { ...d, extracted: { ...d.extracted, engagements: list } };
  });
  const removeEngagementListItem = (engIdx, key, idx) => setData((d) => {
    if (!d) return d;
    const list = [...(d.extracted.engagements || [])];
    const eng = { ...list[engIdx] };
    const sub = [...(eng[key] || [])];
    sub.splice(idx, 1);
    eng[key] = sub;
    list[engIdx] = eng;
    return { ...d, extracted: { ...d.extracted, engagements: list } };
  });
  const addEngagementListItem = (engIdx, key, blank) => setData((d) => {
    if (!d) return d;
    const list = [...(d.extracted.engagements || [])];
    const eng = { ...list[engIdx] };
    eng[key] = [...(eng[key] || []), blank];
    list[engIdx] = eng;
    return { ...d, extracted: { ...d.extracted, engagements: list } };
  });

  const save = async () => {
    if (!data) return;
    const e = data.extracted;
    const engagements = e.engagements || [];
    setSaving(true);
    let activityCount = 0;
    let fuCount = 0;
    let stageMoves = 0;
    let dealsCreated = 0;
    try {
      if (engagements.length === 0) {
        // Fallback: no structured engagements — log a single ungrouped journal activity.
        await api.post("/activities", {
          type: "voice_journal",
          summary: e.summary || "Voice journal",
          details: data.transcript,
        });
        activityCount += 1;
      } else {
        for (const eng of engagements) {
          const entityType = eng.entity_type || "marketing_contact";
          const peopleStr = (eng.people || []).filter((p) => p?.name).map((p) => p.designation ? `${p.name} (${p.designation})` : p.name).join(", ");
          const productsStr = (eng.products || []).filter((p) => p?.name).map((p) => p.name).join(", ");
          const detailParts = [
            eng.discussion ? `Discussion: ${eng.discussion}` : null,
            peopleStr ? `People: ${peopleStr}` : null,
            productsStr ? `Products: ${productsStr}` : null,
            (eng.pain_points || []).length ? `Pain points: ${(eng.pain_points || []).join("; ")}` : null,
            (eng.requirements || []).length ? `Requirements: ${(eng.requirements || []).join("; ")}` : null,
            (eng.objections || []).length ? `Objections: ${(eng.objections || []).join("; ")}` : null,
            `\n— Transcript —\n${data.transcript}`,
          ].filter(Boolean).join("\n");

          // For marketing_contact / lead: create a row in the right collection from the FIRST person on the engagement
          if (entityType === "marketing_contact") {
            const firstPerson = (eng.people || []).find((p) => p?.name);
            if (firstPerson || eng.client_name) {
              try {
                await api.post("/marketing-contacts", {
                  name: firstPerson?.name || eng.client_name || "Unknown",
                  designation: firstPerson?.designation || null,
                  company_name: eng.client_name || null,
                  source: "other",
                  status: "contacted",
                  notes: eng.discussion || null,
                });
              } catch { /* non-fatal — keep going */ }
            }
          } else if (entityType === "lead") {
            const firstPerson = (eng.people || []).find((p) => p?.name);
            if (firstPerson || eng.client_name) {
              try {
                await api.post("/leads", {
                  name: firstPerson?.name || eng.client_name || "Unknown",
                  designation: firstPerson?.designation || null,
                  company_name: eng.client_name || null,
                  source: "other",
                  status: "contacted",
                  notes: eng.discussion || null,
                });
              } catch { /* non-fatal */ }
            }
          }

          // Activity log (always created so the day is recorded)
          await api.post("/activities", {
            type: "voice_journal",
            client_id: entityType === "client" ? (eng.matched_id || null) : null,
            summary: e.summary
              ? `${e.summary} · ${eng.client_name || ""}`
              : `Voice journal — ${eng.client_name || "unknown"} (${entityType.replace("_", " ")})`,
            details: `Type: ${entityType}\n${detailParts}`,
          });
          activityCount += 1;

          // Look up the deal for this client up-front so we can attach
          // both the follow-ups and any stage move to the same deal —
          // without deal_id, follow-ups never show up on the pipeline
          // kanban card (deal.next_followup_date is only updated when a
          // follow-up carries a deal_id).
          let dealForClient = null;
          if (entityType === "client" && eng.matched_id) {
            try {
              const { data: deals } = await api.get("/deals", { params: { client_id: eng.matched_id } });
              // Prefer the most recently updated open deal; fall back to any deal.
              const openDeals = (deals || []).filter((d) => !["won", "lost", "dropped"].includes(d.stage));
              dealForClient = openDeals[0] || (deals || [])[0] || null;
            } catch { /* non-fatal */ }
          }

          // Per-engagement follow-ups (only attached to a CRM client when type === client)
          for (const fu of eng.follow_ups || []) {
            if (!fu.title || !fu.title.trim()) continue;
            const due = fu.due_date
              ? new Date(fu.due_date + "T12:00:00").toISOString()
              : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
            await api.post("/followups", {
              title: fu.with_whom ? `${fu.title} — with ${fu.with_whom}` : fu.title,
              due_date: due,
              client_id: entityType === "client" ? (eng.matched_id || null) : null,
              deal_id: dealForClient?.id || null,
            });
            fuCount += 1;
          }

          // Stage move — when type === client and we matched to a real client.
          // If a deal exists, just move it; if none exists, AUTO-CREATE one at
          // the chosen stage so the engagement shows up on the pipeline kanban.
          if (entityType === "client" && eng.matched_id && eng.stage_suggestion) {
            try {
              if (dealForClient) {
                await api.post(`/deals/${dealForClient.id}/stage`, { stage: eng.stage_suggestion });
                stageMoves += 1;
              } else {
                const { data: newDeal } = await api.post("/deals", {
                  client_id: eng.matched_id,
                  title: eng.client_name || "New opportunity",
                  stage: eng.stage_suggestion,
                  value: 0,
                  notes: eng.discussion || `Auto-created from voice journal · ${new Date().toLocaleDateString()}`,
                });
                dealForClient = newDeal;
                dealsCreated += 1;
              }
            } catch { /* non-fatal */ }
          }
        }
      }

      const parts = [`${activityCount} activit${activityCount === 1 ? "y" : "ies"}`];
      if (fuCount) parts.push(`${fuCount} follow-up${fuCount === 1 ? "" : "s"}`);
      if (dealsCreated) parts.push(`${dealsCreated} deal${dealsCreated === 1 ? "" : "s"} created`);
      if (stageMoves) parts.push(`${stageMoves} stage move${stageMoves === 1 ? "" : "s"}`);
      toast.success(`Day captured · ${parts.join(" · ")}`);
      reset();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const e = data?.extracted;
  const clients = data?.available_clients || [];

  return (
    <div className="space-y-5">
      {/* Step 1 — recorder */}
      <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Step 1 — Record your day</p>

          {!audioBlob ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <button
                type="button"
                onClick={recording ? stop : start}
                className={`h-24 w-24 rounded-full grid place-items-center tap transition-all shadow-lg ${
                  recording
                    ? "bg-red-500 hover:bg-red-600 ring-8 ring-red-200/60 animate-pulse"
                    : "bg-primary hover:bg-primary/90 ring-4 ring-primary-100"
                }`}
                data-testid={recording ? "voice-stop-button" : "voice-record-button"}
                aria-label={recording ? "Stop recording" : "Start recording"}
              >
                {recording ? (
                  <Square className="h-9 w-9 text-white" fill="white" />
                ) : (
                  <Mic className="h-10 w-10 text-primary-foreground" />
                )}
              </button>
              <p className="font-display text-3xl font-black tabular-nums tracking-tight">{fmtTime(seconds)}<span className="text-base text-muted-foreground font-medium"> / 15:00</span></p>
              {recording ? (
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={togglePause} className="rounded-2xl tap" data-testid="voice-pause-button">
                    {paused ? <><Mic className="h-4 w-4 mr-1" /> Resume</> : <><Square className="h-4 w-4 mr-1" /> Pause</>}
                  </Button>
                  <span className="text-xs text-muted-foreground">{paused ? "Paused" : "Recording…"}</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center max-w-sm">
                  Tap to start. Describe everyone you met, what you discussed, what products you shared, and what you owe them next. Up to 15 minutes — auto-stops when you reach the cap.
                </p>
              )}
              {permissionError && (
                <p className="text-xs text-red-700 text-center max-w-sm bg-red-50 rounded-xl p-2">
                  {permissionError}. On iOS Safari you may need to enable mic for this site under Settings → Safari → Camera & Microphone.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <audio src={audioUrl} controls className="w-full" data-testid="voice-audio-preview" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{fmtTime(seconds)} captured</span>
                <Button variant="ghost" size="sm" onClick={reset} className="rounded-2xl tap text-xs" data-testid="voice-reset-button">
                  <X className="h-3.5 w-3.5 mr-1" /> Discard & re-record
                </Button>
                {!data && (
                  <Button onClick={upload} disabled={uploading} className="rounded-2xl tap ml-auto" data-testid="voice-transcribe-button">
                    {uploading ? "Transcribing…" : <><Sparkles className="h-4 w-4 mr-1" /> Transcribe with AI</>}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — review */}
      {data && (
        <>
          {data.transcript && (
            <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
              <CardContent className="p-4 sm:p-6">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Transcript</p>
                <p className="text-sm italic text-muted-foreground whitespace-pre-wrap" data-testid="voice-transcript">{data.transcript}</p>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Step 2 — Review &amp; edit</p>

              <div>
                <Label className="text-xs font-semibold">Day summary</Label>
                <Textarea className="rounded-2xl mt-1.5" rows={2} value={e?.summary || ""} onChange={(ev) => updateExtracted({ summary: ev.target.value })} data-testid="voice-summary" />
              </div>

              {/* Engagements — one card per company */}
              {(e?.engagements || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic py-4 text-center">No engagements detected. The journal will be saved as a single note.</p>
              )}
              {(e?.engagements || []).map((eng, engIdx) => (
                <div key={engIdx} className="rounded-2xl border-2 border-primary-200/60 bg-primary-50/20 p-4 space-y-3" data-testid={`voice-engagement-${engIdx}`}>
                  {/* Engagement header — entity type + company match + remove */}
                  <div className="flex items-start gap-2">
                    <div className="h-9 w-9 rounded-2xl bg-primary text-primary-foreground grid place-items-center shrink-0 text-xs font-bold">
                      #{engIdx + 1}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {/* Entity type radio — Marketing / Lead / Client */}
                      <div className="flex items-center gap-1 rounded-xl border border-border/60 bg-white p-0.5" role="tablist" data-testid={`voice-eng-type-${engIdx}`}>
                        {[
                          { v: "marketing_contact", l: "Marketing" },
                          { v: "lead", l: "Lead" },
                          { v: "client", l: "Client" },
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => updateEngagement(engIdx, { entity_type: opt.v, ...(opt.v !== "client" ? { matched_id: null } : {}) })}
                            className={`flex-1 rounded-lg px-2 py-1 text-[10px] font-bold tap transition ${
                              (eng.entity_type || "marketing_contact") === opt.v
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted/60"
                            }`}
                            data-testid={`voice-eng-${engIdx}-type-${opt.v}`}
                          >
                            {opt.l}
                          </button>
                        ))}
                      </div>
                      <Input
                        value={eng.client_name || ""}
                        onChange={(ev) => updateEngagement(engIdx, { client_name: ev.target.value })}
                        placeholder="Company name"
                        className="rounded-xl h-9 text-sm font-bold"
                        data-testid={`voice-eng-name-${engIdx}`}
                      />
                      {(eng.entity_type || "marketing_contact") === "client" && (
                        <ClientPicker
                          value={eng.matched_id || ""}
                          clients={clients}
                          defaultName={eng.client_name || ""}
                          onChange={(id) => updateEngagement(engIdx, { matched_id: id || null })}
                          onCreated={(c) => {
                            // Append the freshly created client to the in-memory
                            // list so other engagements in the same review can
                            // pick it without a full reload.
                            setData((d) => d ? { ...d, available_clients: [...(d.available_clients || []), c] } : d);
                            updateEngagement(engIdx, {
                              matched_id: c.id,
                              client_name: c.company_name,
                            });
                          }}
                          testIdPrefix={`voice-eng-match-${engIdx}`}
                        />
                      )}
                    </div>
                    <button type="button" onClick={() => removeEngagement(engIdx)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 tap shrink-0" data-testid={`voice-eng-remove-${engIdx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Discussion */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Discussion</Label>
                    <Textarea rows={2} className="rounded-xl mt-1 text-xs" value={eng.discussion || ""} onChange={(ev) => updateEngagement(engIdx, { discussion: ev.target.value })} placeholder="What was discussed at this company" data-testid={`voice-eng-discussion-${engIdx}`} />
                  </div>

                  {/* People */}
                  <div className="rounded-xl border border-blue-200/60 bg-white p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700 inline-flex items-center gap-1 mb-2">
                      <Users2 className="h-3 w-3" /> People met ({(eng.people || []).length})
                    </p>
                    <div className="space-y-1.5">
                      {(eng.people || []).map((p, pIdx) => (
                        <div key={pIdx} className="flex items-start gap-1.5" data-testid={`voice-eng-${engIdx}-person-${pIdx}`}>
                          <Input className="rounded-lg h-8 text-xs flex-1" value={p.name || ""} onChange={(ev) => updateEngagementListItem(engIdx, "people", pIdx, { name: ev.target.value })} placeholder="Name" />
                          <Input className="rounded-lg h-8 text-xs flex-1" value={p.designation || ""} onChange={(ev) => updateEngagementListItem(engIdx, "people", pIdx, { designation: ev.target.value })} placeholder="Designation" />
                          <button type="button" onClick={() => removeEngagementListItem(engIdx, "people", pIdx)} className="h-6 w-6 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 shrink-0">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addEngagementListItem(engIdx, "people", { name: "", designation: "", context: "" })} className="h-7 rounded-xl text-[11px]" data-testid={`voice-eng-${engIdx}-add-person`}>
                        <Plus className="h-3 w-3 mr-1" /> Add person
                      </Button>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="rounded-xl border border-violet-200/60 bg-white p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 inline-flex items-center gap-1 mb-2">
                      <Package className="h-3 w-3" /> Products discussed ({(eng.products || []).length})
                    </p>
                    <div className="space-y-1.5">
                      {(eng.products || []).map((p, pIdx) => (
                        <div key={pIdx} className="flex items-start gap-1.5" data-testid={`voice-eng-${engIdx}-product-${pIdx}`}>
                          <Input className="rounded-lg h-8 text-xs flex-1" value={p.name || ""} onChange={(ev) => updateEngagementListItem(engIdx, "products", pIdx, { name: ev.target.value })} placeholder="Product name" />
                          <Input className="rounded-lg h-8 text-xs flex-1" value={p.context || ""} onChange={(ev) => updateEngagementListItem(engIdx, "products", pIdx, { context: ev.target.value })} placeholder="Context (use case)" />
                          <button type="button" onClick={() => removeEngagementListItem(engIdx, "products", pIdx)} className="h-6 w-6 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 shrink-0">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addEngagementListItem(engIdx, "products", { name: "", context: "", matched_id: null })} className="h-7 rounded-xl text-[11px]" data-testid={`voice-eng-${engIdx}-add-product`}>
                        <Plus className="h-3 w-3 mr-1" /> Add product
                      </Button>
                    </div>
                  </div>

                  {/* Follow-ups */}
                  <div className="rounded-xl border border-amber-200/60 bg-white p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 inline-flex items-center gap-1 mb-2">
                      <ListChecks className="h-3 w-3" /> Follow-ups ({(eng.follow_ups || []).length})
                    </p>
                    <div className="space-y-2">
                      {(eng.follow_ups || []).map((f, fIdx) => (
                        <div key={fIdx} className="rounded-lg border border-amber-100 p-2 space-y-1.5" data-testid={`voice-eng-${engIdx}-fu-${fIdx}`}>
                          <Input className="rounded-lg h-8 text-xs" value={f.title || ""} onChange={(ev) => updateEngagementListItem(engIdx, "follow_ups", fIdx, { title: ev.target.value })} placeholder="Action" />
                          <div className="grid grid-cols-2 gap-1.5">
                            <Select value={f.with_whom || "__none"} onValueChange={(v) => updateEngagementListItem(engIdx, "follow_ups", fIdx, { with_whom: v === "__none" ? "" : v })}>
                              <SelectTrigger className="rounded-lg h-8 text-xs"><SelectValue placeholder="With whom" /></SelectTrigger>
                              <SelectContent className="rounded-2xl">
                                <SelectItem value="__none">No specific person</SelectItem>
                                {(eng.people || []).filter((p) => p.name).map((p, idx) => <SelectItem key={idx} value={p.name}>{p.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input type="date" className="rounded-lg h-8 text-xs" value={f.due_date || ""} onChange={(ev) => updateEngagementListItem(engIdx, "follow_ups", fIdx, { due_date: ev.target.value })} />
                          </div>
                          <div className="flex justify-end">
                            <button type="button" onClick={() => removeEngagementListItem(engIdx, "follow_ups", fIdx)} className="h-6 w-6 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addEngagementListItem(engIdx, "follow_ups", { title: "", with_whom: "", due_date: "", channel: "call" })} className="h-7 rounded-xl text-[11px]" data-testid={`voice-eng-${engIdx}-add-fu`}>
                        <Plus className="h-3 w-3 mr-1" /> Add follow-up
                      </Button>
                    </div>
                  </div>

                  {/* Pipeline stage move */}
                  <div className="rounded-xl border border-emerald-200/60 bg-white p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 inline-flex items-center gap-1 mb-2">
                      <GitBranch className="h-3 w-3" /> Pipeline stage
                    </p>
                    <Select
                      value={eng.stage_suggestion || "__none"}
                      onValueChange={(v) => updateEngagement(engIdx, { stage_suggestion: v === "__none" ? null : v })}
                    >
                      <SelectTrigger className="rounded-lg h-9 text-xs" data-testid={`voice-eng-${engIdx}-stage`}><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="__none">Don't change pipeline stage</SelectItem>
                        {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{STAGE_LABEL[s.key] || s.key}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {eng.stage_reason && <p className="text-[10px] text-muted-foreground italic mt-1.5">"{eng.stage_reason}"</p>}
                    {!eng.matched_id && eng.stage_suggestion && (
                      <p className="text-[10px] text-amber-700 mt-1">No CRM match — pipeline card won't be created on save.</p>
                    )}
                    {eng.matched_id && eng.stage_suggestion && (
                      <p className="text-[10px] text-emerald-700 mt-1">Will create or move a deal at this stage on save → visible on Pipeline.</p>
                    )}
                  </div>
                </div>
              ))}

              <Button
                variant="outline"
                onClick={() => updateExtracted({
                  engagements: [
                    ...(e?.engagements || []),
                    { client_name: "", matched_id: null, entity_type: "marketing_contact", discussion: "", people: [], products: [], follow_ups: [], stage_suggestion: null, stage_reason: null, pain_points: [], requirements: [], objections: [], sentiment: "neutral" }
                  ]
                })}
                className="w-full rounded-2xl tap"
                data-testid="voice-add-engagement"
              >
                <Plus className="h-4 w-4 mr-1" /> Add another company
              </Button>

              <Button onClick={save} disabled={saving} className="w-full h-12 rounded-2xl tap" data-testid="voice-save-button">
                <CheckCircle2 className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Confirm & save day"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function AIExtract() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "meeting");
  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">AI capture</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Update your memory in seconds.</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">AI extracts structured data. <strong>You always confirm</strong> before saving.</p>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-2xl mb-5 flex flex-wrap h-auto">
          <TabsTrigger value="voice-journal" className="rounded-xl" data-testid="ai-tab-voice-journal"><AudioLines className="h-3.5 w-3.5 mr-1" />Voice journal</TabsTrigger>
          <TabsTrigger value="meeting" className="rounded-xl" data-testid="ai-tab-meeting"><Mic className="h-3.5 w-3.5 mr-1" />Meeting summary</TabsTrigger>
          <TabsTrigger value="meeting-bulk" className="rounded-xl" data-testid="ai-tab-meeting-bulk"><Layers className="h-3.5 w-3.5 mr-1" />Bulk meetings</TabsTrigger>
          <TabsTrigger value="vcard" className="rounded-xl" data-testid="ai-tab-vcard"><Camera className="h-3.5 w-3.5 mr-1" />Visiting card</TabsTrigger>
          <TabsTrigger value="vcard-bulk" className="rounded-xl" data-testid="ai-tab-vcard-bulk"><Layers className="h-3.5 w-3.5 mr-1" />Bulk cards</TabsTrigger>
        </TabsList>
        <TabsContent value="voice-journal"><VoiceJournalFlow /></TabsContent>
        <TabsContent value="meeting"><MeetingFlow /></TabsContent>
        <TabsContent value="meeting-bulk"><BulkMeetingFlow /></TabsContent>
        <TabsContent value="vcard"><VisitingCardFlow /></TabsContent>
        <TabsContent value="vcard-bulk"><BulkVisitingCardFlow /></TabsContent>
      </Tabs>
    </div>
  );
}
